import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { AlertOctagon, CheckCircle2, ShieldCheck, TriangleAlert } from "lucide-react";
import { api } from "@/lib/api";
import { Panel } from "@/components/common/Panel";
import { TiltCard } from "@/components/common/TiltCard";
import { AnimatedNumber } from "@/components/common/AnimatedNumber";
import { staggerContainer, staggerItem } from "@/lib/motion";

const tone = (v) =>
    v === null ? { text: "text-slate-500", ring: "rgba(148,163,184,0.35)", Icon: ShieldCheck }
        : v >= 75 ? { text: "text-emerald-400", ring: "var(--safe)", Icon: CheckCircle2 }
            : v >= 45 ? { text: "text-amber-400", ring: "var(--warning)", Icon: TriangleAlert }
                : { text: "text-rose-400", ring: "var(--neon-red)", Icon: AlertOctagon };

function ReadinessDial({ value }) {
    const t = tone(value);
    const pct = value ?? 0;
    const R = 26;
    const C = 2 * Math.PI * R;
    return (
        <div className="relative w-[68px] h-[68px] shrink-0">
            <svg viewBox="0 0 64 64" className="w-full h-full -rotate-90">
                <circle cx="32" cy="32" r={R} fill="none" stroke="rgba(148,163,184,0.12)" strokeWidth="4" />
                <motion.circle
                    cx="32" cy="32" r={R} fill="none" stroke={t.ring} strokeWidth="4" strokeLinecap="round"
                    strokeDasharray={C}
                    initial={{ strokeDashoffset: C }}
                    animate={{ strokeDashoffset: C - (C * pct) / 100 }}
                    transition={{ duration: 1.1, ease: [0.22, 1, 0.36, 1] }}
                    style={{ filter: `drop-shadow(0 0 6px ${t.ring})` }}
                />
            </svg>
            <div className="absolute inset-0 flex items-center justify-center">
                <span className={`font-display text-base ${t.text}`}>
                    {value === null ? "—" : <AnimatedNumber value={Math.round(value)} />}
                </span>
            </div>
        </div>
    );
}

export default function Compliance() {
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const nav = useNavigate();

    useEffect(() => {
        (async () => {
            try {
                const { data } = await api.get("/posture/compliance");
                setData(data);
            } finally {
                setLoading(false);
            }
        })();
    }, []);

    const frameworks = data?.frameworks || [];
    const inScope = frameworks.filter((f) => f.in_scope);
    const worst = inScope[0];

    return (
        <motion.div variants={staggerContainer} initial="initial" animate="animate" className="space-y-6">
            <motion.div variants={staggerItem}>
                <Panel eyebrow="Scope" title="What these numbers are made of" testId="compliance-scope-panel">
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                        <div>
                            <div className="label-eyebrow mb-1">Frameworks in scope</div>
                            <div className="font-display text-3xl text-white">
                                <AnimatedNumber value={inScope.length} />
                                <span className="text-slate-400 text-lg"> / {frameworks.length}</span>
                            </div>
                        </div>
                        <div>
                            <div className="label-eyebrow mb-1">Findings mapped to a framework</div>
                            <div className="font-display text-3xl text-white">
                                <AnimatedNumber value={data?.total_findings_in_scope || 0} />
                            </div>
                        </div>
                        <div>
                            <div className="label-eyebrow mb-1">Weakest posture</div>
                            <div className="font-display text-3xl text-rose-400">{worst ? worst.code : "—"}</div>
                        </div>
                    </div>
                    <p className="text-xs text-slate-400 mt-4 leading-relaxed">
                        Readiness is a coverage ratio, not an estimate: the share of your in-scope control surface —
                        parsed artifacts plus engineered roles — that is completely clean for that framework's tag
                        vocabulary, minus a capped severity penalty. Every card shows its own numerator and denominator.
                    </p>
                </Panel>
            </motion.div>

            <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
                {loading && (
                    <Panel className="md:col-span-2 xl:col-span-3" eyebrow="Loading" title="Cross-referencing frameworks…">
                        <div className="h-16" />
                    </Panel>
                )}
                {frameworks.map((fw) => {
                    const t = tone(fw.readiness);
                    return (
                        <motion.div key={fw.code} variants={staggerItem}>
                            <TiltCard
                                max={5}
                                testId={`compliance-framework-${fw.code}`}
                                onClick={() => nav("/governance/map")}
                                className="group panel-elevated card-lift p-5 rounded-[4px] cursor-pointer h-full"
                            >
                                <div className="flex items-start gap-4" style={{ transform: "translateZ(26px)" }}>
                                    <ReadinessDial value={fw.readiness} />
                                    <div className="min-w-0 flex-1">
                                        <div className="flex items-center gap-2">
                                            <t.Icon size={13} className={t.text} />
                                            <span className="font-mono text-xs uppercase tracking-wider text-white">{fw.code}</span>
                                        </div>
                                        <div className="text-[13px] text-slate-300 mt-1 leading-snug">{fw.name}</div>
                                        <div className="font-mono text-[10px] uppercase tracking-wider text-slate-400 mt-1">{fw.region}</div>
                                    </div>
                                </div>

                                <p className="text-[11px] text-slate-400 mt-4 leading-relaxed" style={{ transform: "translateZ(14px)" }}>
                                    {fw.basis}
                                </p>

                                <div className="flex items-center gap-3 mt-3 pt-3 border-t border-white/5 font-mono text-[10px] uppercase tracking-wider">
                                    <button
                                        data-testid={`compliance-findings-${fw.code}`}
                                        title={
                                            fw.engines?.length === 1
                                                ? `Open Findings filtered to the ${fw.engines[0]} engine — the detection source in ${fw.code} scope`
                                                : "Open the Findings centre"
                                        }
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            nav(fw.engines?.length === 1 ? `/findings?engine=${fw.engines[0]}` : "/findings");
                                        }}
                                        className="text-slate-400 hover:text-cyan-300 transition-colors"
                                    >
                                        {fw.relevant_findings} finding{fw.relevant_findings === 1 ? "" : "s"}
                                    </button>
                                    <span className="text-slate-700">·</span>
                                    <button
                                        data-testid={`compliance-conflicts-${fw.code}`}
                                        title="Open the Sentinel Forge role library to inspect the conflicting roles"
                                        onClick={(e) => { e.stopPropagation(); nav("/roles"); }}
                                        className="text-slate-400 hover:text-rose-300 transition-colors"
                                    >
                                        {fw.relevant_sod_conflicts} SoD hit{fw.relevant_sod_conflicts === 1 ? "" : "s"}
                                    </button>
                                </div>
                            </TiltCard>
                        </motion.div>
                    );
                })}
            </div>
        </motion.div>
    );
}
